<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { formatVisibility } from '@/js/core/utils/formatters';
import { deleteTray } from '@/js/core/utils/trays-api';
import { useFocalPoint } from '@/js/core/composables/useFocalPoint';
import { useTrayEdit } from '@/js/core/composables/useTrayEdit';
import TrayEditForm from '@/js/core/components/parts/trays/TrayEditForm.vue';
import PersonNameLink from '@/js/core/components/parts/PersonNameLink.vue';

const props = defineProps({
  tray: {
    type: Object,
    required: true,
  },
  commentCount: {
    type: Number,
    default: 0,
  },
  // The profile whose panel is currently open, and the panel's id — used so each
  // trigger can advertise aria-expanded/aria-controls correctly.
  activeProfileUuid: {
    type: String,
    default: null,
  },
  personPanelId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update', 'deleted', 'left', 'person-click']);

const handlePersonClick = (uuid, buttonEl) => emit('person-click', uuid, buttonEl);

const {
  isEditing,
  isSaving,
  settingsInitialised,
  hasSettingsDirty,
  editError,
  errorField,
  fieldErrors,
  editTitle,
  editDescription,
  editVisibility,
  editCommentAccess,
  editVisibleUsers,
  editCommentableUsers,
  editImageUrl,
  editFocalPoint,
  editFileName,
  editAlt,
  editCaption,
  editFeaturedMediaAltError,
  isUploading,
  uploadError,
  handleEdit: startEdit,
  cancelEdit,
  saveEdit,
  onSaveComplete: completeSave,
  onSaveError,
  handleFileChange,
  handleFileDrop,
  handleDragOver,
  handleRemoveImage,
  updateEditAlt,
} = useTrayEdit(() => props.tray, emit);

const editFormRef = ref(null);
const editBtnRef = ref(null);
const deleteBtnRef = ref(null);
const confirmCancelRef = ref(null);
const showDeleteConfirm = ref(false);
const isDeleting = ref(false);
const deleteError = ref('');

const canEdit = () => props.tray.is_owner === true || props.tray.is_manager === true;

const { focalPointStyle } = useFocalPoint(() => props.tray.focal_point);

const accessUserList = computed(() => props.tray.access_user_profiles ?? []);

const showVisibleUsers = computed(
  () => props.tray.visibility === 'members' && accessUserList.value.length > 0
);

const deleteWarning =
  "Are you sure you'd like to delete this Tray? It will no longer be visible on GSD Now and you will not be able to access any of its contents.";

const openDeleteConfirm = async () => {
  deleteError.value = '';
  showDeleteConfirm.value = true;
  await nextTick();
  confirmCancelRef.value?.focus();
};

const closeDeleteConfirm = async () => {
  showDeleteConfirm.value = false;
  await nextTick();
  deleteBtnRef.value?.focus();
};

watch(isEditing, async open => {
  await nextTick();

  if (open) {
    editFormRef.value?.focusFirst();
    return;
  }

  editBtnRef.value?.focus();
});

const handleDelete = async () => {
  isDeleting.value = true;
  deleteError.value = '';

  try {
    await deleteTray(props.tray.uuid);
    emit('deleted');
  } catch (err) {
    deleteError.value = err.message || 'Failed to delete Tray';
    isDeleting.value = false;
  }
};

const handleClose = () => {
  const isDirty =
    settingsInitialised.value && (hasSettingsDirty.value || editFormRef.value?.hasPendingChanges);

  if (isDirty && !confirm('You have unsaved changes. Are you sure you want to close?')) {
    return;
  }

  cancelEdit();
};

// Settings save landed; commit the queued manager changes before leaving edit mode.
const onSaveComplete = async () => {
  const result = await editFormRef.value?.runManagerSaves();

  if (result?.selfRemoved) {
    emit('left');
    return;
  }

  if (result && !result.allSucceeded) {
    isSaving.value = false;
    return;
  }

  completeSave();
};

defineExpose({ onSaveComplete, onSaveError, isEditing, isSaving });
</script>

<template>
  <div class="view-tray-modal__detail">
    <template v-if="!isEditing">
      <div v-if="tray.featured_media_url" class="view-tray-modal__featured-media">
        <img
          :src="tray.featured_media_display?.url || tray.featured_media_url"
          :srcset="tray.featured_media_display?.srcset || undefined"
          :sizes="tray.featured_media_display?.srcset ? '100vw' : undefined"
          :width="tray.featured_media_display?.width || undefined"
          :height="tray.featured_media_display?.height || undefined"
          :alt="tray.featured_media_alt || tray.title"
          class="view-tray-modal__featured-image"
          :style="focalPointStyle"
          fetchpriority="high"
          decoding="async"
          @contextmenu.prevent
        />
        <p v-if="tray.featured_media_caption" class="view-tray-modal__featured-caption">
          {{ tray.featured_media_caption }}
        </p>
      </div>

      <div class="view-tray-modal__info-section">
        <div class="view-tray-modal__metadata">
          <div
            v-if="tray.content"
            class="view-tray-modal__metadata-content"
            v-html="tray.content"
          />
        </div>

        <div class="view-tray-modal__info-box">
          <div class="view-tray-modal__info-item author">
            <div class="view-tray-modal__info-label">Started by</div>
            <div class="view-tray-modal__info-value">
              <PersonNameLink
                :name="tray.author?.name || 'Unknown'"
                :profile-uuid="tray.author?.profile_uuid"
                :expanded="!!activeProfileUuid && activeProfileUuid === tray.author?.profile_uuid"
                :controls="personPanelId"
                @click="handlePersonClick"
              />
            </div>
          </div>
          <div class="view-tray-modal__info-item visible">
            <div class="view-tray-modal__info-label">Visible to</div>
            <div class="view-tray-modal__info-value">
              {{ formatVisibility(tray.visibility)
              }}<template v-if="showVisibleUsers"
                >. This Tray is visible to you and the following users:</template
              >
            </div>
            <div v-if="showVisibleUsers" class="view-tray-modal__visible-users-block">
              <ul class="view-tray-modal__visible-users">
                <li
                  v-for="(user, index) in accessUserList"
                  :key="user.profile_uuid || index"
                  class="view-tray-modal__visible-user"
                >
                  <PersonNameLink
                    :name="user.name"
                    :profile-uuid="user.profile_uuid"
                    :expanded="!!activeProfileUuid && activeProfileUuid === user.profile_uuid"
                    :controls="personPanelId"
                    @click="handlePersonClick"
                  />
                </li>
              </ul>
            </div>
          </div>

          <div v-if="canEdit() && !showDeleteConfirm" class="view-tray-modal__info-actions">
            <button
              ref="editBtnRef"
              type="button"
              class="view-tray-modal__action-btn--edit"
              @click="startEdit"
            >
              Edit
            </button>
            <button
              ref="deleteBtnRef"
              type="button"
              class="view-tray-modal__action-btn--delete"
              @click="openDeleteConfirm"
            >
              Delete
            </button>
          </div>

          <div
            v-else-if="showDeleteConfirm"
            class="view-tray-modal__delete-confirm"
            role="alert"
            @keydown.esc.stop="closeDeleteConfirm"
          >
            <p class="view-tray-modal__delete-confirm-message">{{ deleteWarning }}</p>
            <p v-if="deleteError" class="view-tray-modal__delete-confirm-error">
              {{ deleteError }}
            </p>
            <div class="view-tray-modal__delete-confirm-actions">
              <button
                type="button"
                class="view-tray-modal__action-btn--delete"
                :disabled="isDeleting"
                @click="handleDelete"
              >
                {{ isDeleting ? 'Deleting...' : 'Yes, delete' }}
              </button>
              <button
                ref="confirmCancelRef"
                type="button"
                class="view-tray-modal__delete-confirm-cancel"
                :disabled="isDeleting"
                @click="closeDeleteConfirm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <TrayEditForm
      v-else
      ref="editFormRef"
      :tray="tray"
      :settings-initialised="settingsInitialised"
      :edit-title="editTitle"
      :edit-description="editDescription"
      :edit-visibility="editVisibility"
      :edit-comment-access="editCommentAccess"
      :edit-visible-users="editVisibleUsers"
      :edit-commentable-users="editCommentableUsers"
      :edit-image-url="editImageUrl"
      :edit-focal-point="editFocalPoint"
      :edit-file-name="editFileName"
      :edit-alt="editAlt"
      :edit-caption="editCaption"
      :edit-featured-media-alt-error="editFeaturedMediaAltError"
      :edit-error="editError"
      :error-field="errorField"
      :field-errors="fieldErrors"
      :upload-error="uploadError"
      :is-uploading="isUploading"
      :is-saving="isSaving"
      @update:edit-title="editTitle = $event"
      @update:edit-description="editDescription = $event"
      @update:edit-visibility="editVisibility = $event"
      @update:edit-comment-access="editCommentAccess = $event"
      @update:edit-visible-users="editVisibleUsers = $event"
      @update:edit-commentable-users="editCommentableUsers = $event"
      @update:edit-focal-point="editFocalPoint = $event"
      @update:edit-alt="updateEditAlt"
      @update:edit-caption="editCaption = $event"
      @save="saveEdit"
      @cancel="handleClose"
      @file-change="handleFileChange"
      @file-drop="handleFileDrop"
      @drag-over="handleDragOver"
      @remove-image="handleRemoveImage"
    />
  </div>
</template>
